// src/components/compliance/modals/StaleControlsModal.jsx

import React from "react";
import {
  safeNumber,
  formatTimestamp,
  formatRelativeTime,
  normalizeText,
  cleanValue,
  includesNormalized,
  buildControlLabel,
  getDisplayStateLabel,
  getSummaryChipStyle,
  getTaskStatusLabel,
} from "../../../utils/complianceFormatters";
import { findTaskForItem } from "../../../utils/remediationTaskHelpers";
import ModalShell from "./shared/ModalShell";
import MetricTile from "./shared/MetricTile";
import ModalSection from "./shared/ModalSection";
import TaskQueueCard from "./shared/TaskQueueCard";

function matchesFocus(item = {}, context = {}) {
  if (!context) return false;

  const frameworkMatch = !context.framework
    ? true
    : normalizeText(item.framework) === normalizeText(context.framework);

  const controlMatch =
    !context.controlId ||
    normalizeText(item.control_id) === normalizeText(context.controlId) ||
    includesNormalized(item.control_id, context.controlId);

  const ruleMatch =
    !context.ruleId ||
    normalizeText(item.rule_id) === normalizeText(context.ruleId) ||
    includesNormalized(item.rule_id, context.ruleId);

  const resourceMatch =
    !context.resourceId || includesNormalized(item.resource_id, context.resourceId);

  const hasAnyScopedField = Boolean(
    context.framework || context.controlId || context.ruleId || context.resourceId
  );

  return hasAnyScopedField && frameworkMatch && controlMatch && ruleMatch && resourceMatch;
}

function getStaleAgeLabel(item = {}) {
  if (item.age_days !== undefined && item.age_days !== null) {
    return `${safeNumber(item.age_days)}d old`;
  }

  return formatRelativeTime(item.last_checked_at || item.captured_at || item.updated_at);
}

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 16,
};

export default function StaleControlsModal({
  open,
  framework,
  staleControls = [],
  activeRemediationTasks = [],
  effectiveFocusContext = {},
  onClose,
  onViewTask,
  onFixPlan,
  onViewGuidance,
  setMode,
}) {
  if (!open) return null;

  const openChildModal = (callback) => {
    onClose?.();
    window.setTimeout(() => {
      callback?.();
    }, 0);
  };

  const controls = Array.isArray(staleControls) ? staleControls : [];
  const focusedCount = controls.filter((item) => matchesFocus(item, effectiveFocusContext)).length;
  const linkedTaskCount = controls.filter((item) =>
    findTaskForItem(activeRemediationTasks, item, framework)
  ).length;
  const withoutTaskCount = Math.max(0, controls.length - linkedTaskCount);
  const oldestAge = controls.reduce((maxAge, item) => {
    const age = Number(item.age_days);
    return Number.isFinite(age) ? Math.max(maxAge, age) : maxAge;
  }, 0);

  return (
    <ModalShell
      className="oasys-stale-controls-modal"
      open={open}
      ariaLabel="Stale controls"
      eyebrow="OASYS · FRESHNESS DEBT"
      title="Stale Controls"
      subtitle="Review controls where evidence is old, missing, or needs a fresh source check before posture can be trusted as current."
      onClose={onClose}
      maxWidth="1080px"
      accentBorder="rgba(74, 222, 128, 0.45)"
      zIndex={135}
    >
      <div style={metricGridStyle}>
        <MetricTile label="Stale Controls" value={controls.length} sub={`for ${framework}`} color="#60a5fa" />
        <MetricTile label="Linked Tasks" value={linkedTaskCount} sub={`${withoutTaskCount} without task`} color="#34d399" />
        <MetricTile
          label="Focused Matches"
          value={focusedCount}
          sub="selected context"
          color={focusedCount > 0 ? "#fbbf24" : "#60a5fa"}
        />
        <MetricTile
          label="Oldest Evidence"
          value={oldestAge > 0 ? `${oldestAge}d` : "—"}
          sub="maximum age returned"
          color={oldestAge > 0 ? "#fbbf24" : "#60a5fa"}
        />
      </div>

      <ModalSection
        title="Stale Control Activity"
        description="Showing the latest stale evidence items returned from the posture freshness feed."
        badge={`${controls.length} shown`}
        badgeTone="ok"
      >
        {controls.length === 0 ? (
          <div className="muted" style={{ padding: 14 }}>
            No stale controls are currently returned for this framework.
          </div>
        ) : (
          controls.map((item, index) => {
            const controlId = item.control_id || item.controlId || "—";
            const ruleId = item.rule_id || item.ruleId || "";
            const resourceId = item.resource_id || item.resourceId || "";
            const linkedTask = findTaskForItem(activeRemediationTasks, item, framework);
            const isFocused = matchesFocus(item, effectiveFocusContext);
            const staleAge = getStaleAgeLabel(item);
            const checkedAt = formatTimestamp(
              item.last_checked_at || item.captured_at || item.updated_at
            );
            const stateLabel = getDisplayStateLabel(
              item.freshness_state || item.freshness || "stale"
            );

            return (
              <TaskQueueCard
                key={`${controlId}-${ruleId}-${resourceId}-${index}`}
                title={buildControlLabel(controlId, ruleId)}
                dueLabel={checkedAt}
                chips={[
                  { label: stateLabel, tone: "warn" },
                  linkedTask
                    ? { label: `Task: ${getTaskStatusLabel(linkedTask.status)}`, tone: "warn", strong: false }
                    : null,
                  isFocused ? { label: "Selected Context", tone: "accent", strong: false } : null,
                ]}
                meta={[
                  { label: "Framework", value: cleanValue(item.framework, framework) },
                  { label: "Resource", value: cleanValue(resourceId, "—"), wrap: true },
                  { label: "Age", value: cleanValue(staleAge, "—") },
                  { label: "Checked", value: checkedAt },
                ]}
                context={cleanValue(
                  item.reason_summary || item.summary || item.description,
                  "Evidence is stale or needs a fresh assessment."
                )}
                actions={[
                  linkedTask
                    ? {
                        key: "review",
                        label: "Review / Manage",
                        onClick: (event) => {
                          event?.stopPropagation?.();
                          openChildModal(() => onViewTask(linkedTask, { returnTo: "stale_controls" }));
                        },
                      }
                    : null,
                  {
                    key: "fix-plan",
                    label: "Fix Plan",
                    onClick: (event) => {
                      event?.stopPropagation?.();
                      openChildModal(() => onFixPlan(item, "stale", { returnTo: "stale_controls" }));
                    },
                  },
                  {
                    key: "guidance",
                    label: "Guidance",
                    onClick: (event) => {
                      event?.stopPropagation?.();
                      openChildModal(() => onViewGuidance(item, { returnTo: "stale_controls" }));
                    },
                  },
                  {
                    key: "drift",
                    label: "Open in Drift",
                    onClick: () => {
                      onClose?.();
                      setMode("drift", {
                        framework: item.framework || framework,
                        controlId,
                        ruleId: ruleId || undefined,
                        resourceId: resourceId || undefined,
                      });
                    },
                  },
                ]}
              />
            );
          })
        )}

        {controls.length > 0 ? (
          <span style={{ ...getSummaryChipStyle("ok"), justifySelf: "start", fontWeight: 900 }}>
            {controls.length} shown
          </span>
        ) : null}
      </ModalSection>
    </ModalShell>
  );
}
